import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listingsApi, bookingsApi } from "@/lib/api";
import { gbp, nextDays } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const BOOKING_TYPE = {
  services: "slot",
  experiences: "slot",
  education: "session",
  food: "order",
  rentals: "daterange",
  casting: "project",
};
const SLOT_MODULES = ["services", "experiences", "education"];

export function BookingWidget({ listing }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const services = listing?.meta?.services || [];
  const needsSlot = SLOT_MODULES.includes(listing.module);
  const quoteMode = !needsSlot; // food / rentals / casting → enquiry-first on web v1

  const offlineMethod = listing.accepts_cash ? "cash" : "bank_transfer";
  const offlineLabel = listing.accepts_cash ? "Reserve — pay on arrival" : "Reserve — pay by bank transfer";

  const [serviceId, setServiceId] = useState(services[0]?.id || null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [slots, setSlots] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const days = useMemo(() => nextDays(14), []);
  const selectedService = services.find((s) => s.id === serviceId);
  const price = selectedService?.price ?? listing.price_from ?? listing.price;

  useEffect(() => {
    if (!needsSlot || !date) {
      setSlots(null);
      return;
    }
    let active = true;
    setSlots(null);
    setSlot(null);
    listingsApi
      .slots(listing.id, date, serviceId || undefined)
      .then((d) => active && setSlots(d))
      .catch(() => active && setSlots({ slots: [] }));
    return () => {
      active = false;
    };
  }, [needsSlot, date, serviceId, listing.id]);

  const canProceed = quoteMode ? !!date : !!date && !!slot;

  const buildBooking = () => ({
    listing_id: listing.id,
    module: listing.module,
    booking_type: BOOKING_TYPE[listing.module] || "slot",
    service_id: serviceId || undefined,
    service_name: selectedService?.name || undefined,
    date,
    time_slot: needsSlot ? slot : undefined,
    quantity: 1,
  });

  const requireAuth = () => {
    if (!user) {
      navigate("/login", { state: { from: `/listing/${listing.id}` } });
      return false;
    }
    if (!canProceed) {
      toast.error(quoteMode ? "Pick a date first." : "Pick a date and time first.");
      return false;
    }
    return true;
  };

  const reserveOffline = async (method) => {
    if (!requireAuth()) return;
    setSubmitting(true);
    try {
      await bookingsApi.offline(buildBooking(), method);
      if (method === "quote") toast.success("Request sent — the provider will reply with details.");
      else if (method === "cash") toast.success("Booked! Pay on arrival. See it in My bookings.");
      else toast.success("Reserved! Complete your bank transfer to confirm.");
      navigate("/bookings");
    } catch (err) {
      toast.error(err.message || "Could not complete booking");
    } finally {
      setSubmitting(false);
    }
  };

  const payCard = async () => {
    if (!requireAuth()) return;
    setSubmitting(true);
    try {
      const res = await bookingsApi.stripeSession(buildBooking(), window.location.origin);
      if (res.url) window.location.href = res.url;
      else {
        toast.error("Could not start payment");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.message || "Could not start payment");
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-lg" data-testid="booking-widget">
      <div className="flex items-baseline justify-between">
        <span className="font-mono-num text-2xl font-bold">{gbp(price)}</span>
        <span className="text-sm text-muted-foreground">/{listing.price_unit}</span>
      </div>

      {services.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Choose a service</p>
          <div className="space-y-2">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => setServiceId(s.id)}
                data-testid={`service-option-${s.id}`}
                className={`flex w-full items-center justify-between rounded-xl border px-3.5 py-3 text-left text-sm transition-colors ${
                  s.id === serviceId ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                }`}
              >
                <span>
                  <span className="font-medium">{s.name}</span>
                  {s.duration && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock size={12} /> {s.duration}m
                    </span>
                  )}
                </span>
                <span className="font-mono-num font-semibold">{gbp(s.price)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {quoteMode ? "Preferred date" : "Pick a date"}
        </p>
        <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
          {days.map((d) => (
            <button
              key={d.iso}
              onClick={() => setDate(d.iso)}
              data-testid={`date-${d.iso}`}
              className={`flex min-w-[56px] flex-col items-center rounded-xl border px-2 py-2 transition-colors ${
                d.iso === date ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"
              }`}
            >
              <span className="text-[11px] uppercase">{d.weekday}</span>
              <span className="font-mono-num text-base font-bold">{d.day}</span>
              <span className="text-[10px]">{d.month}</span>
            </button>
          ))}
        </div>
      </div>

      {needsSlot && date && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pick a time</p>
          {slots === null ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 size={15} className="animate-spin" /> Loading times…
            </div>
          ) : slots.closed || !(slots.slots || []).length ? (
            <p className="py-3 text-sm text-muted-foreground">No availability on this day. Try another date.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {slots.slots.map((s) => (
                <button
                  key={s.time}
                  disabled={!s.available}
                  onClick={() => setSlot(s.time)}
                  data-testid={`slot-${s.time}`}
                  className={`rounded-lg border py-2 text-sm font-mono-num transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
                    s.time === slot ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"
                  }`}
                >
                  {s.time}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-5 space-y-2">
        {quoteMode ? (
          <Button
            onClick={() => reserveOffline("quote")}
            disabled={submitting || !canProceed}
            className="w-full rounded-full"
            data-testid="request-btn"
          >
            {submitting ? "Sending…" : "Request to book"}
          </Button>
        ) : (
          <>
            <Button onClick={payCard} disabled={submitting || !canProceed} className="w-full rounded-full" data-testid="pay-card-btn">
              Reserve with card
            </Button>
            <Button
              onClick={() => reserveOffline(offlineMethod)}
              disabled={submitting || !canProceed}
              variant="outline"
              className="w-full rounded-full"
              data-testid="reserve-btn"
            >
              {submitting ? "Booking…" : offlineLabel}
            </Button>
          </>
        )}
      </div>
      <p className="mt-3 text-center text-[11px] text-muted-foreground">
        {quoteMode ? "No payment taken now — the provider confirms details first." : "Free cancellation subject to the provider's policy."}
      </p>
    </div>
  );
}
